import { BodyMD, BodySM, Heading3XL, Heading4XL } from "../../../typography";
import { useEffect, useRef, useState } from "react";

import { cn } from "@verse_core/shared_components/ClassName";

export type StatItemProps = {
  value: string;
  description: string;
  titleClassName?: string;
  descriptionClassName?: string;
  itemClassName?: string;
  disableAnimation?: boolean;
};

export const StatItem = ({
  value,
  description,
  titleClassName,
  descriptionClassName,
  itemClassName,
  disableAnimation = false,
}: StatItemProps) => {
  const [count, setCount] = useState(0);
  const [floatCount, setFloatCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statRef = useRef<HTMLDivElement>(null);

  // Detect if value is in the format X.Y/Z (e.g., 4.8/5)
  const matchFraction = value.match(/([0-9]+\.[0-9]+)\/(\d+)/);
  const matchDecimal = value.match(/([0-9]+\.[0-9]+)/);
  const isRange = value.includes("-");
  let targetValue: number;
  let rangeStart: number | undefined;
  let prefix = "";
  let suffix = "";
  let fractionSuffix = "";
  let isFraction = false;
  let isDecimal = false;

  if (matchFraction) {
    // e.g., 4.8/5
    targetValue = parseFloat(matchFraction[1]);
    fractionSuffix = "/" + matchFraction[2];
    isFraction = true;
    prefix = value.match(/^[^0-9]*/)?.[0] || "";
    suffix = value.match(/[^0-9\/]*$/)?.[0] || "";
  } else if (matchDecimal) {
    // e.g., 99.9%
    targetValue = parseFloat(matchDecimal[1]);
    isDecimal = true;
    prefix = value.match(/^[^0-9]*/)?.[0] || "";
    suffix = value.match(/[^0-9]*$/)?.[0] || "";
  } else if (isRange) {
    const [start, end] = value
      .split("-")
      .map((num) => parseInt(num.replace(/[^0-9]/g, "")));
    rangeStart = start;
    targetValue = end;
    prefix = value.match(/^[^0-9]*/)?.[0] || "";
    suffix = value.match(/[^0-9]*$/)?.[0] || "";
  } else {
    targetValue = parseInt(value.replace(/[^0-9]/g, ""));
    prefix = value.match(/^[^0-9]*/)?.[0] || "";
    suffix = value.match(/[^0-9]*$/)?.[0] || "";
  }

  useEffect(() => {
    if (disableAnimation) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);

          let startTime: number;
          let animationFrame: number;
          const duration = 2000; // 2 seconds animation

          const animate = (currentTime: number) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);

            // Easing function for smooth animation
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            if (isFraction || isDecimal) {
              const currentFloat = parseFloat(
                (easeOutQuart * targetValue).toFixed(1),
              );
              setFloatCount(currentFloat);
            } else {
              const currentCount = Math.floor(easeOutQuart * targetValue);
              setCount(currentCount);
            }

            if (progress < 1) {
              animationFrame = requestAnimationFrame(animate);
            }
          };

          animationFrame = requestAnimationFrame(animate);

          return () => {
            cancelAnimationFrame(animationFrame);
          };
        }
      },
      { threshold: 0.1 },
    );

    if (statRef.current) {
      observer.observe(statRef.current);
    }

    return () => {
      if (statRef.current) {
        observer.unobserve(statRef.current);
      }
    };
  }, [targetValue, hasAnimated, disableAnimation]);

  let displayValue;
  if (disableAnimation) {
    displayValue = value;
  } else if (isFraction) {
    displayValue = `${prefix}${hasAnimated ? floatCount.toFixed(1) : "0.0"}${fractionSuffix}${suffix}`;
  } else if (isDecimal) {
    displayValue = `${prefix}${hasAnimated ? floatCount.toFixed(1) : "0.0"}${suffix}`;
  } else if (isRange) {
    displayValue = `${prefix}${rangeStart}-${count}${suffix}`;
  } else {
    displayValue = `${prefix}${count}${suffix}`;
  }

  return (
    <div
      ref={statRef}
      className={cn("tw-flex tw-flex-col tw-items-center", itemClassName)}
    >
      <Heading4XL
        className={cn(
          "tw-my-0 tw-hidden tw-text-white min440:tw-flex",
          titleClassName,
        )}
      >
        {displayValue}
      </Heading4XL>
      <BodyMD
        className={cn(
          "tw-mb-0 tw-hidden tw-text-center tw-text-white min440:tw-flex",
          descriptionClassName,
        )}
      >
        {description}
      </BodyMD>

      <Heading3XL
        className={cn(
          "tw-my-0 tw-flex tw-text-white min440:tw-hidden",
          titleClassName,
        )}
      >
        {displayValue}
      </Heading3XL>
      <BodySM
        className={cn(
          "tw-mb-0 tw-flex tw-text-center tw-text-white min440:tw-hidden",
          descriptionClassName,
        )}
      >
        {description}
      </BodySM>
    </div>
  );
};

export type StatsProps = {
  className?: string;
  stats: { value: string; description: string; disableAnimation?: boolean }[];
  titleClassName?: string;
  descriptionClassName?: string;
  itemClassName?: string;
};

export const StatsSection = ({
  className,
  stats,
  titleClassName,
  descriptionClassName,
  itemClassName,
}: StatsProps) => (
  <div
    className={cn(
      "tw-grid tw-w-full tw-grid-cols-2 tw-gap-[1.5em] tw-text-white",
      className,
    )}
  >
    {stats.map((stat) => (
      <StatItem
        key={stat.value}
        value={stat.value}
        description={stat.description}
        titleClassName={titleClassName}
        descriptionClassName={descriptionClassName}
        itemClassName={itemClassName}
        disableAnimation={stat.disableAnimation}
      />
    ))}
  </div>
);
